// src/pages/admin/Notifications.jsx - Complete Updated Version
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Search, Check, CheckCheck, Trash2, RefreshCw, Calendar, Package,
  FileText, CreditCard, Tag, TrendingUp, MessageSquare, AlertCircle,
  Download, Eye, Users, Building2, Upload, Send, ChevronDown, ChevronUp,
  Clock, Link as LinkIcon, X as XIcon
} from 'lucide-react';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import notificationApi from '../../api/notification-api';
import contractApi from '../../api/contract-api';
import { authApi } from '../../api/auth-api';
import client from '../../api/apollo-client';
import { GET_USERS_PAGINATED, GET_REGIONS, GET_VILLES_BY_REGION } from '../../api/users-api';

const GET_ALL_PRODUCTS = gql`
  query GetAllProducts {
    products { id name price image }
  }
`;

const GET_PROMOTION_REGIONS_BY_PRODUCT = gql`
  query ActivePromotionRegionsByProduct($productId: ID!) {
    activePromotionRegionsByProduct(productId: $productId) {
      id nomRegion
    }
  }
`;

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [markingAsRead, setMarkingAsRead] = useState(new Set());
  const [deleting, setDeleting] = useState(new Set());
  const [downloadingContract, setDownloadingContract] = useState(new Set());
  const [uploadingContract, setUploadingContract] = useState(new Set());

  // Create notification panel
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifDesc, setNotifDesc] = useState('');
  const [selectedUserTypes, setSelectedUserTypes] = useState(new Set(['ALL']));
  const [notifRegionIds, setNotifRegionIds] = useState(new Set());
  const [notifVilleIds, setNotifVilleIds] = useState(new Set());
  const [regions, setRegions] = useState([]);
  const [villes, setVilles] = useState([]); // all villes for selected regions, flat
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingVilles, setLoadingVilles] = useState(false);
  const [notifScheduled, setNotifScheduled] = useState(false);
  const [notifScheduledAt, setNotifScheduledAt] = useState('');
  const [withPush, setWithPush] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [testMode, setTestMode] = useState(false);

  // Phone number targeting (test mode + specific production targeting)
  const [phoneSearch, setPhoneSearch] = useState('');
  const [phoneSearchResults, setPhoneSearchResults] = useState([]);
  const [loadingPhoneSearch, setLoadingPhoneSearch] = useState(false);
  const [selectedTargetUsers, setSelectedTargetUsers] = useState([]); // [{id, tel, firstName, lastName}]
  const phoneSearchRef = useRef(null);

  // Product link state
  const [linkProduct, setLinkProduct] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [promotionRegions, setPromotionRegions] = useState([]); // regions where product has active promos
  const [loadingPromoRegions, setLoadingPromoRegions] = useState(false);
  const [selectedPromoRegionIds, setSelectedPromoRegionIds] = useState(new Set());

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  // Load regions when panel opens
  useEffect(() => {
    if (!showCreatePanel || regions.length > 0) return;
    setLoadingRegions(true);
    client.query({ query: GET_REGIONS, fetchPolicy: 'cache-first' })
      .then(({ data }) => setRegions(data?.regions || []))
      .catch(() => {})
      .finally(() => setLoadingRegions(false));
  }, [showCreatePanel]);

  // Load villes for all selected regions
  useEffect(() => {
    if (notifRegionIds.size === 0) { setVilles([]); setNotifVilleIds(new Set()); return; }
    setLoadingVilles(true);
    Promise.all([...notifRegionIds].map(regionId =>
      client.query({ query: GET_VILLES_BY_REGION, variables: { regionId }, fetchPolicy: 'cache-first' })
        .then(({ data }) => data?.villesByRegion || [])
        .catch(() => [])
    )).then(results => {
      const seen = new Set();
      const merged = results.flat().filter(v => { if (seen.has(v.id)) return false; seen.add(v.id); return true; });
      merged.sort((a, b) => a.nomVilleVillage.localeCompare(b.nomVilleVillage));
      setVilles(merged);
      // Remove selected villes that are no longer in the available list
      setNotifVilleIds(prev => new Set([...prev].filter(id => merged.some(v => v.id === id))));
    }).finally(() => setLoadingVilles(false));
  }, [notifRegionIds]);

  // Phone search debounce
  useEffect(() => {
    if (!phoneSearch.trim()) { setPhoneSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setLoadingPhoneSearch(true);
      try {
        const { data } = await client.query({
          query: GET_USERS_PAGINATED,
          variables: { usersQueryInput: { searchTerm: phoneSearch.trim(), limit: 10, page: 1 } },
          fetchPolicy: 'network-only',
        });
        const results = (data?.usersPaginated?.users || []).filter(
          u => !selectedTargetUsers.some(s => s.id === u.id)
        );
        setPhoneSearchResults(results);
      } catch { setPhoneSearchResults([]); }
      finally { setLoadingPhoneSearch(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [phoneSearch]);

  // Load all products when panel opens
  useEffect(() => {
    if (!showCreatePanel || allProducts.length > 0) return;
    setLoadingProducts(true);
    client.query({ query: GET_ALL_PRODUCTS, fetchPolicy: 'cache-first' })
      .then(({ data }) => setAllProducts(data?.products || []))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, [showCreatePanel]);

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setPromotionRegions([]);
    setSelectedPromoRegionIds(new Set());
    if (!product) return;
    setLoadingPromoRegions(true);
    try {
      const { data } = await client.query({
        query: GET_PROMOTION_REGIONS_BY_PRODUCT,
        variables: { productId: product.id },
        fetchPolicy: 'network-only',
      });
      const regions = data?.activePromotionRegionsByProduct || [];
      setPromotionRegions(regions);
      setSelectedPromoRegionIds(new Set(regions.map(r => r.id)));
    } catch (err) { console.error('activePromotionRegionsByProduct error:', err); setPromotionRegions([]); }
    finally { setLoadingPromoRegions(false); }
  };

  const togglePromoRegion = (regionId) => {
    setSelectedPromoRegionIds(prev => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId); else next.add(regionId);
      return next;
    });
  };

  const toggleUserType = (value) => {
    if (value === 'ALL') {
      setSelectedUserTypes(new Set(['ALL']));
    } else {
      setSelectedUserTypes(prev => {
        const next = new Set(prev);
        next.delete('ALL');
        if (next.has(value)) {
          next.delete(value);
          if (next.size === 0) next.add('ALL');
        } else {
          next.add(value);
        }
        return next;
      });
    }
  };

  const toggleRegion = (regionId) => {
    setNotifRegionIds(prev => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId); else next.add(regionId);
      return next;
    });
  };

  const toggleVille = (villeId) => {
    setNotifVilleIds(prev => {
      const next = new Set(prev);
      if (next.has(villeId)) next.delete(villeId); else next.add(villeId);
      return next;
    });
  };

  const fetchAllUsersOfType = async (userType, regionId) => {
    const allUsers = [];
    let page = 1;
    while (true) {
      const queryInput = { limit: 100, page };
      if (userType) queryInput.userType = userType;
      if (regionId) queryInput.regionId = regionId;
      const { data } = await client.query({
        query: GET_USERS_PAGINATED,
        variables: { usersQueryInput: queryInput },
        fetchPolicy: 'network-only',
      });
      const users = data?.usersPaginated?.users || [];
      allUsers.push(...users);
      if (!data?.usersPaginated?.pagination?.hasNextPage) break;
      page++;
    }
    return allUsers;
  };

  const sendNotifications = async () => {
    const title = notifTitle.trim();
    const desc = notifDesc.trim();
    if (!title) { toast.error('Le titre est requis'); return; }

    const message = desc ? `${title} : ${desc}` : title;
    setIsSending(true);

    try {
      // ── Test mode OR specific targeting: send only to selected users ──
      if (testMode || selectedTargetUsers.length > 0) {
        if (selectedTargetUsers.length === 0) {
          toast.error('Sélectionnez au moins un numéro de téléphone');
          setIsSending(false);
          return;
        }
        const prefix = testMode ? '[TEST] ' : '';
        const results = await Promise.allSettled(
          selectedTargetUsers.map(u =>
            notificationApi.createNotification({
              notificationType: 'SYSTEM_ALERT',
              message: `${prefix}${message}`,
              userId: u.id,
              withPush,
              ...(linkProduct && selectedProduct ? { productId: selectedProduct.id } : {}),
            })
          )
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        const sent = selectedTargetUsers.length - failed;
        const names = selectedTargetUsers.map(u => u.tel || `${u.firstName} ${u.lastName}`).join(', ');
        if (failed === 0) {
          toast.success(`Notification${testMode ? ' de test' : ''} envoyée à ${sent} destinataire${sent > 1 ? 's' : ''} : ${names}`);
        } else {
          toast.warn(`${sent} envoyé${sent > 1 ? 's' : ''}, ${failed} échec${failed > 1 ? 's' : ''}`);
        }
        setIsSending(false);
        return;
      }

      const typesToFetch = selectedUserTypes.has('ALL')
        ? ['LABORATORY', 'PHARMACIST', 'SELLER', 'AGENT_TELEPHONIQUE', 'RESPONSABLE_ACHAT']
        : [...selectedUserTypes];

      // When a product with promotion regions is selected, target those selected promo regions.
      // Otherwise, target the manually selected regions (or all if none selected).
      const usePromoRegions = linkProduct && selectedProduct && promotionRegions.length > 0;
      const regionIdsToTarget = usePromoRegions
        ? [...selectedPromoRegionIds]
        : notifRegionIds.size > 0 ? [...notifRegionIds] : [null];

      const allUsersArrays = await Promise.all(
        regionIdsToTarget.flatMap(regionId =>
          typesToFetch.map(type => fetchAllUsersOfType(type, regionId))
        )
      );

      const seen = new Set();
      const users = allUsersArrays.flat().filter(u => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        // If not promo-region mode and villes are selected, filter by ville
        if (!usePromoRegions && notifVilleIds.size > 0 && !notifVilleIds.has(u.pharmacy?.villeId)) return false;
        return true;
      });

      if (users.length === 0) {
        toast.warn('Aucun utilisateur trouvé pour ces critères');
        setIsSending(false);
        return;
      }

      const doSend = async () => {
        const results = await Promise.allSettled(
          users.map(user =>
            notificationApi.createNotification({
              notificationType: 'SYSTEM_ALERT',
              message,
              userId: user.id,
              withPush,
              ...(linkProduct && selectedProduct ? { productId: selectedProduct.id } : {}),
            })
          )
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed === 0) {
          toast.success(`Notification envoyée à ${users.length} utilisateur${users.length > 1 ? 's' : ''}`);
        } else {
          toast.warn(`${users.length - failed} envoyé${users.length - failed > 1 ? 's' : ''}, ${failed} échec${failed > 1 ? 's' : ''}`);
        }
      };

      if (notifScheduled && notifScheduledAt) {
        const delay = new Date(notifScheduledAt) - new Date();
        if (delay <= 0) {
          await doSend();
        } else {
          setTimeout(doSend, delay);
          toast.success(`Notification planifiée pour le ${new Date(notifScheduledAt).toLocaleString('fr-FR')}`);
        }
      } else {
        await doSend();
      }

      setNotifTitle('');
      setNotifDesc('');
      setSelectedUserTypes(new Set(['ALL']));
      setNotifRegionIds(new Set());
      setNotifVilleIds(new Set());
      setVilles([]);
      setNotifScheduled(false);
      setNotifScheduledAt('');
      setWithPush(false);
      setLinkProduct(false);
      setSelectedProduct(null);
      setPromotionRegions([]);
      setSelectedPromoRegionIds(new Set());
      setSelectedTargetUsers([]);
      setPhoneSearch('');
      setPhoneSearchResults([]);
      setShowCreatePanel(false);
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error("Erreur lors de l'envoi des notifications");
    } finally {
      setIsSending(false);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const userData = authApi.getUserData();
      if (!userData?.id) {
        console.error('No user data found');
        return;
      }

      const data = await notificationApi.getUserNotifications(userData.id, 50, 0, filter);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    setMarkingAsRead(prev => new Set([...prev, notificationId]));
    try {
      await notificationApi.markNotificationSeen(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, seen: true }
            : notif
        )
      );
      toast.success('Notification marquée comme lue');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const userData = authApi.getUserData();
      if (!userData?.id) return;

      await notificationApi.markAllNotificationsSeen(userData.id);
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, seen: true }))
      );
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteNotification = async (notificationId) => {
    setDeleting(prev => new Set([...prev, notificationId]));
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Handle contract download for lab interface
  const handleDownloadContract = async (notification) => {
    const notificationId = notification.id;
    setDownloadingContract(prev => new Set([...prev, notificationId]));
    
    try {
      // Extract contract information from notification message
      const contractInfo = extractContractInfo(notification.message);
      
      if (!contractInfo.contractNumber) {
        toast.error('Impossible d\'extraire les informations du contrat');
        return;
      }

      // Download the contract using the contract API
      const response = await fetch(`/api/contracts/download/${contractInfo.contractNumber}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download contract');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat-${contractInfo.contractNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Contrat téléchargé avec succès');
      
      // Mark notification as read after successful download
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Erreur lors du téléchargement du contrat');
    } finally {
      setDownloadingContract(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Handle signed contract upload for lab interface
  const handleUploadSignedContract = async (notification) => {
    const notificationId = notification.id;
    
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.style.display = 'none';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (file.type !== 'application/pdf') {
        toast.error('Seuls les fichiers PDF sont acceptés');
        return;
      }

      setUploadingContract(prev => new Set([...prev, notificationId]));
      
      try {
        const contractInfo = extractContractInfo(notification.message);
        
        if (!contractInfo.contractNumber) {
          toast.error('Impossible d\'extraire les informations du contrat');
          return;
        }

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('signedContract', file);
        formData.append('contractNumber', contractInfo.contractNumber);
        formData.append('laboratoryName', contractInfo.laboratoryName || 'Laboratoire');
        formData.append('promotions', contractInfo.promotions || 'Non spécifiées');

        // Upload signed contract
        const response = await fetch('/api/contracts/upload-signed', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload signed contract');
        }

        const result = await response.json();
        
        toast.success('Contrat signé envoyé avec succès');
        
        // Mark notification as read
        await markAsRead(notificationId);
        
      } catch (error) {
        console.error('Error uploading signed contract:', error);
        toast.error('Erreur lors de l\'envoi du contrat signé');
      } finally {
        setUploadingContract(prev => {
          const newSet = new Set(prev);
          newSet.delete(notificationId);
          return newSet;
        });
      }
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  // Handle signed contract download for admin interface
  const handleDownloadSignedContract = async (notification) => {
    const notificationId = notification.id;
    setDownloadingContract(prev => new Set([...prev, notificationId]));
    
    try {
      const contractInfo = extractSignedContractInfo(notification.message);
      
      if (!contractInfo.contractNumber) {
        toast.error('Impossible d\'extraire les informations du contrat');
        return;
      }

      // Use the contract API helper to download the signed contract
      await contractApi.downloadSignedContract(contractInfo.contractNumber);
      
      toast.success('Contrat signé téléchargé avec succès');
      
      // Mark notification as read after successful download
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error downloading signed contract:', error);
      if (error.message === 'No signed contract found') {
        toast.error('Aucun contrat signé trouvé pour ce numéro de contrat');
      } else {
        toast.error('Erreur lors du téléchargement du contrat signé');
      }
    } finally {
      setDownloadingContract(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Helper function to extract contract info from new contract notification message
  const extractContractInfo = (message) => {
    // Extract contract info from notification message
    // Format: "Nouveau contrat créé: ContractName (ContractNumber). Promotions associées: PromoNames."
    const contractMatch = message.match(/Nouveau contrat créé: (.+?) \((.+?)\)/);
    const promotionsMatch = message.match(/Promotions associées: (.+?)\./);
    
    return {
      laboratoryName: contractMatch ? contractMatch[1] : null,
      contractNumber: contractMatch ? contractMatch[2] : null,
      promotions: promotionsMatch ? promotionsMatch[1] : null
    };
  };

  // Helper function to extract contract info from signed contract notification message
  const extractSignedContractInfo = (message) => {
    // Extract contract info from signed contract notification
    const contractMatch = message.match(/Contrat signé reçu de (.+?) \((.+?)\)/);
    const promotionsMatch = message.match(/Promotions: (.+?)\./);
    
    return {
      laboratoryName: contractMatch ? contractMatch[1] : null,
      contractNumber: contractMatch ? contractMatch[2] : null,
      promotions: promotionsMatch ? promotionsMatch[1] : null
    };
  };

  const getNotificationIcon = (type) => {
    const iconProps = { size: 18, className: "text-gray-600" };
    switch (type) {
      case 'USER_REGISTRATION':
        return <Users {...iconProps} />;
      case 'PAYMENT_REQUEST':
        return <CreditCard {...iconProps} />;
      case 'LABORATORY_ACTIVITY':
        return <Building2 {...iconProps} />;
      case 'CONTRACT_ALERT':
        return <FileText {...iconProps} />;
      case 'SYSTEM_ALERT':
        return <AlertCircle {...iconProps} />;
      default:
        return <Bell {...iconProps} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'USER_REGISTRATION':
        return 'border-l-blue-500 bg-blue-50';
      case 'PAYMENT_REQUEST':
        return 'border-l-green-500 bg-green-50';
      case 'LABORATORY_ACTIVITY':
        return 'border-l-purple-500 bg-purple-50';
      case 'CONTRACT_ALERT':
        return 'border-l-orange-500 bg-orange-50';
      case 'SYSTEM_ALERT':
        return 'border-l-red-500 bg-red-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getActionButton = (notification) => {
    const { notificationType, message, id } = notification;
    
    // Signed contract notifications (for admin)
    if (notificationType === 'CONTRACT_ALERT' && message.includes('Contrat signé reçu')) {
      return (
        <div className="flex space-x-2 mt-2">
          <button 
            onClick={() => handleDownloadSignedContract(notification)}
            disabled={downloadingContract.has(id)}
            className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Download size={12} className="mr-1" />
            {downloadingContract.has(id) ? 'Téléchargement...' : 'Télécharger contrat signé'}
          </button>
          <button 
            onClick={() => {/* Handle contract review/approval logic */}}
            className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
          >
            <Eye size={12} className="mr-1" />
            Examiner
          </button>
        </div>
      );
    }
    
    // New contract creation notifications (for lab)
    if (notificationType === 'CONTRACT_ALERT' && message.includes('Nouveau contrat créé')) {
      return (
        <div className="flex space-x-2 mt-2">
          <button 
            onClick={() => handleDownloadContract(notification)}
            disabled={downloadingContract.has(id)}
            className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Download size={12} className="mr-1" />
            {downloadingContract.has(id) ? 'Téléchargement...' : 'Télécharger'}
          </button>
          <button 
            onClick={() => handleUploadSignedContract(notification)}
            disabled={uploadingContract.has(id)}
            className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Upload size={12} className="mr-1" />
            {uploadingContract.has(id) ? 'Envoi...' : 'Renvoyer signé'}
          </button>
        </div>
      );
    }

    // Contract notifications with download/sign/upload actions (generic)
    if (notificationType === 'CONTRACT_ALERT' || notificationType === 'CONTRACT_RENEWAL') {
      if (message.includes('télécharger, signer et renvoyer')) {
        return (
          <div className="flex space-x-2 mt-2">
            <button 
              onClick={() => handleDownloadContract(notification)}
              disabled={downloadingContract.has(id)}
              className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Download size={12} className="mr-1" />
              {downloadingContract.has(id) ? 'Téléchargement...' : 'Télécharger'}
            </button>
            <button 
              onClick={() => handleUploadSignedContract(notification)}
              disabled={uploadingContract.has(id)}
              className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Upload size={12} className="mr-1" />
              {uploadingContract.has(id) ? 'Envoi...' : 'Renvoyer signé'}
            </button>
          </div>
        );
      }
    }
    
    // Payment request notifications
    if (notificationType === 'PAYMENT_REQUEST') {
      return (
        <button
          onClick={() => navigate('/admin/payment-requests', {
            state: { openRequestId: notification.relatedId }
          })}
          className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors mt-2"
        >
          <CreditCard size={12} className="mr-1" />
          Traiter la demande
        </button>
      );
    }

    // Payment notifications
    if (notificationType === 'PAYMENT_RECEIVED') {
      return (
        <button className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors mt-2">
          <Eye size={12} className="mr-1" />
          Voir détails
        </button>
      );
    }
    
    return null;
  };

  const formatDate = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffInMinutes = Math.floor((now - notifDate) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `Il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = searchTerm === '' || 
      notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.notificationType.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const unreadCount = notifications.filter(n => !n.seen).length;

  return (
    <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <Bell className="mr-2 sm:mr-3 text-blue-600" size={22} />
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1 text-sm hidden sm:block">
              Gérez vos notifications et alertes importantes
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={loadNotifications}
              className="flex items-center px-3 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <RefreshCw size={14} className="mr-1.5" />
              <span className="hidden sm:inline">Actualiser</span>
            </button>

            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center px-3 py-2 text-sm text-green-600 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
              >
                <CheckCheck size={14} className="mr-1.5" />
                <span className="hidden sm:inline">Tout marquer comme lu</span>
                <span className="sm:hidden">Tout lire</span>
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher dans les notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Toutes</option>
            <option value="unread">Non lues</option>
            <option value="CONTRACT_ALERT">Contrats</option>
            <option value="PAYMENT_REQUEST">Paiements</option>
            <option value="LABORATORY_ACTIVITY">Laboratoires</option>
          </select>
        </div>
      </div>

      {/* Create Notification Panel */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <button
          onClick={() => setShowCreatePanel(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Send size={15} className="text-blue-600" />
            </div>
            <span className="font-semibold text-gray-800">Créer une notification</span>
          </div>
          {showCreatePanel ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </button>

        {showCreatePanel && (
          <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={notifTitle}
                onChange={e => setNotifTitle(e.target.value)}
                placeholder="Ex: Mise à jour importante"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={notifDesc}
                onChange={e => setNotifDesc(e.target.value)}
                placeholder="Détails supplémentaires (optionnel)…"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Recipients — user types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de destinataires</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'ALL', label: 'Tous' },
                  { value: 'LABORATORY', label: 'Laboratoire' },
                  { value: 'PHARMACIST', label: 'Pharmacien' },
                  { value: 'PARAPHARMACIST', label: 'Parapharmacien' },
                  { value: 'SELLER', label: 'Vendeur' },
                  { value: 'AGENT_TELEPHONIQUE', label: 'Agent téléphonique' },
                  { value: 'RESPONSABLE_ACHAT', label: 'Responsable achat' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleUserType(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      selectedUserTypes.has(opt.value)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Geographic filter — Regions (multi-select) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Régions <span className="text-gray-400 font-normal">(optionnel — toutes si aucune sélectionnée)</span>
                </label>
                {notifRegionIds.size > 0 && (
                  <button type="button" onClick={() => setNotifRegionIds(new Set())} className="text-xs text-red-500 hover:underline">
                    Effacer ({notifRegionIds.size})
                  </button>
                )}
              </div>
              {loadingRegions ? (
                <p className="text-xs text-gray-400">Chargement…</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                  {regions.map(r => (
                    <label key={r.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${notifRegionIds.has(r.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                      <input type="checkbox" className="hidden" checked={notifRegionIds.has(r.id)} onChange={() => toggleRegion(r.id)} />
                      {r.nomRegion}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Geographic filter — Villes (multi-select, filtered by selected regions) */}
            {notifRegionIds.size > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Villes <span className="text-gray-400 font-normal">(optionnel — toutes les villes des régions si aucune sélectionnée)</span>
                  </label>
                  {notifVilleIds.size > 0 && (
                    <button type="button" onClick={() => setNotifVilleIds(new Set())} className="text-xs text-red-500 hover:underline">
                      Effacer ({notifVilleIds.size})
                    </button>
                  )}
                </div>
                {loadingVilles ? (
                  <p className="text-xs text-gray-400">Chargement…</p>
                ) : villes.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Aucune ville trouvée</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                    {villes.map(v => (
                      <label key={v.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${notifVilleIds.has(v.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                        <input type="checkbox" className="hidden" checked={notifVilleIds.has(v.id)} onChange={() => toggleVille(v.id)} />
                        {v.nomVilleVillage}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Push toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl border-2 transition-colors cursor-pointer select-none"
              style={{ borderColor: withPush ? '#2563eb' : '#e5e7eb', background: withPush ? '#eff6ff' : '#f9fafb' }}
              onClick={() => setWithPush(v => !v)}
            >
              <div className="flex items-center gap-2">
                <Bell size={15} className={withPush ? 'text-blue-600' : 'text-gray-400'} />
                <div>
                  <p className={`text-sm font-medium ${withPush ? 'text-blue-700' : 'text-gray-700'}`}>
                    Notification push
                  </p>
                  <p className="text-xs text-gray-400">
                    {withPush ? 'In-app + push sur l\'appareil mobile' : 'In-app uniquement'}
                  </p>
                </div>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${withPush ? 'bg-blue-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${withPush ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
            </div>

            {/* Schedule toggle */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifScheduled}
                  onChange={e => setNotifScheduled(e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                />
                <Clock size={15} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Planifier l'envoi</span>
              </label>
              {notifScheduled && (
                <input
                  type="datetime-local"
                  value={notifScheduledAt}
                  onChange={e => setNotifScheduledAt(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>

            {/* Product link toggle */}
            <div className="space-y-2">
              <div
                className="flex items-center justify-between p-3 rounded-xl border-2 transition-colors cursor-pointer select-none"
                style={{ borderColor: linkProduct ? '#7c3aed' : '#e5e7eb', background: linkProduct ? '#f5f3ff' : '#f9fafb' }}
                onClick={() => { setLinkProduct(v => !v); if (linkProduct) { setSelectedProduct(null); setPromotionRegions([]); setSelectedPromoRegionIds(new Set()); } }}
              >
                <div className="flex items-center gap-2">
                  <LinkIcon size={15} className={linkProduct ? 'text-purple-600' : 'text-gray-400'} />
                  <div>
                    <p className={`text-sm font-medium ${linkProduct ? 'text-purple-700' : 'text-gray-700'}`}>Lier à un produit</p>
                    <p className="text-xs text-gray-400">{linkProduct ? 'La notification ouvrira la fiche produit' : 'Optionnel — navigation directe vers un produit'}</p>
                  </div>
                </div>
                <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${linkProduct ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${linkProduct ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
              </div>

              {linkProduct && (
                <div className="space-y-2">
                  {/* Product dropdown */}
                  <div className="relative">
                    <Package size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <select
                      value={selectedProduct?.id || ''}
                      onChange={e => {
                        const p = allProducts.find(x => x.id === e.target.value) || null;
                        handleProductSelect(p);
                      }}
                      disabled={loadingProducts}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:bg-gray-50 appearance-none"
                    >
                      <option value="">{loadingProducts ? 'Chargement…' : '— Sélectionner un produit —'}</option>
                      {allProducts.map(p => (
                        <option key={p.id} value={p.id}>{p.name} — {p.price} DA</option>
                      ))}
                    </select>
                  </div>

                  {/* Promotion regions */}
                  {selectedProduct && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-2">
                      {loadingPromoRegions ? (
                        <p className="text-xs text-purple-500">Chargement des régions avec promotion active…</p>
                      ) : promotionRegions.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">Aucune promotion active pour ce produit — la notification sera envoyée selon les critères région/ville ci-dessus.</p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-purple-700">Régions avec promotion active</p>
                            <button
                              type="button"
                              onClick={() => setSelectedPromoRegionIds(
                                selectedPromoRegionIds.size === promotionRegions.length
                                  ? new Set()
                                  : new Set(promotionRegions.map(r => r.id))
                              )}
                              className="text-xs text-purple-600 hover:underline"
                            >
                              {selectedPromoRegionIds.size === promotionRegions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {promotionRegions.map(r => (
                              <label key={r.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${selectedPromoRegionIds.has(r.id) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}>
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={selectedPromoRegionIds.has(r.id)}
                                  onChange={() => togglePromoRegion(r.id)}
                                />
                                {r.nomRegion}
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-purple-600">{selectedPromoRegionIds.size} région{selectedPromoRegionIds.size !== 1 ? 's' : ''} sélectionnée{selectedPromoRegionIds.size !== 1 ? 's' : ''}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Test mode toggle */}
            <div
              className={`flex items-center justify-between p-3 rounded-xl border-2 transition-colors cursor-pointer select-none ${testMode ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}
              onClick={() => { setTestMode(v => !v); setSelectedTargetUsers([]); setPhoneSearch(''); setPhoneSearchResults([]); }}
            >
              <div className="flex items-center gap-2">
                <AlertCircle size={15} className={testMode ? 'text-amber-600' : 'text-gray-400'} />
                <div>
                  <p className={`text-sm font-medium ${testMode ? 'text-amber-700' : 'text-gray-700'}`}>Mode test</p>
                  <p className="text-xs text-gray-400">
                    {testMode
                      ? 'Envoi uniquement aux numéros sélectionnés ci-dessous — aucun autre utilisateur ne sera notifié'
                      : 'Activer pour tester sur des numéros spécifiques avant un envoi général'}
                  </p>
                </div>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${testMode ? 'bg-amber-500' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${testMode ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
            </div>

            {/* Phone number targeting — visible in test mode OR always accessible */}
            <div className={`rounded-xl border-2 p-3 space-y-2 ${testMode ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <p className={`text-sm font-medium ${testMode ? 'text-amber-700' : 'text-gray-700'}`}>
                  {testMode ? 'Numéros de test' : 'Destinataires spécifiques'}
                  <span className="ml-1 text-xs font-normal text-gray-400">(optionnel en mode production)</span>
                </p>
                {selectedTargetUsers.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setSelectedTargetUsers([]); setPhoneSearch(''); setPhoneSearchResults([]); }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Effacer tout ({selectedTargetUsers.length})
                  </button>
                )}
              </div>

              {/* Selected users chips */}
              {selectedTargetUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedTargetUsers.map(u => (
                    <span
                      key={u.id}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${testMode ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {u.tel || `${u.firstName} ${u.lastName}`}
                      <button
                        type="button"
                        onClick={() => setSelectedTargetUsers(prev => prev.filter(x => x.id !== u.id))}
                        className="ml-0.5 hover:opacity-70"
                      >
                        <XIcon size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search input */}
              <div className="relative" ref={phoneSearchRef}>
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={phoneSearch}
                  onChange={e => setPhoneSearch(e.target.value)}
                  placeholder="Rechercher par numéro ou nom…"
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
                {loadingPhoneSearch && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Search results dropdown */}
              {phoneSearchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg bg-white shadow-sm divide-y divide-gray-100 max-h-44 overflow-y-auto">
                  {phoneSearchResults.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setSelectedTargetUsers(prev => [...prev, { id: u.id, tel: u.tel, firstName: u.firstName, lastName: u.lastName }]);
                        setPhoneSearch('');
                        setPhoneSearchResults([]);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-xs font-medium text-gray-800">{u.tel}</span>
                      {(u.firstName || u.lastName) && (
                        <span className="ml-2 text-xs text-gray-500">{u.firstName} {u.lastName}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {phoneSearch.trim() && !loadingPhoneSearch && phoneSearchResults.length === 0 && (
                <p className="text-xs text-gray-400 italic">Aucun résultat</p>
              )}
            </div>

            {/* Send button */}
            <div className="flex justify-end items-center gap-3">
              {testMode && selectedTargetUsers.length > 0 && (
                <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                  Test → {selectedTargetUsers.length} numéro{selectedTargetUsers.length > 1 ? 's' : ''}
                </span>
              )}
              {!testMode && selectedTargetUsers.length > 0 && (
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Ciblé → {selectedTargetUsers.length} destinataire{selectedTargetUsers.length > 1 ? 's' : ''}
                </span>
              )}
              <button
                onClick={sendNotifications}
                disabled={isSending || !notifTitle.trim() || (testMode && selectedTargetUsers.length === 0)}
                className={`flex items-center gap-2 px-5 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${testMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <Send size={15} />
                {isSending ? 'Envoi en cours…' : testMode ? 'Envoyer (test)' : notifScheduled && notifScheduledAt ? 'Planifier' : 'Envoyer'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Chargement des notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune notification</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Aucune notification ne correspond à votre recherche.' : 'Vous n\'avez aucune notification pour le moment.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg shadow border-l-4 p-4 transition-all hover:shadow-md ${getNotificationColor(notification.notificationType)} ${!notification.seen ? 'ring-2 ring-blue-200' : ''}`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.notificationType)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {notification.notificationType.replace('_', ' ')}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(notification.createdAt)}
                      </span>
                      {!notification.seen && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-1">
                    {notification.message}
                  </p>
                  
                  {/* Action buttons */}
                  {getActionButton(notification)}
                  
                  {/* Mark as read / Delete buttons */}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                    <div className="flex space-x-2">
                      {!notification.seen && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          disabled={markingAsRead.has(notification.id)}
                          className="flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          <Check size={12} className="mr-1" />
                          {markingAsRead.has(notification.id) ? 'Marquage...' : 'Marquer comme lu'}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      disabled={deleting.has(notification.id)}
                      className="flex items-center px-2 py-1 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      <Trash2 size={12} className="mr-1" />
                      {deleting.has(notification.id) ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;